// Orquestación de la compactación del Chat —
// ADR-2026-09-13-el-chat-se-compacta-y-se-puede-buscar.
//
// `compactacionDeContexto.ts` mide y corta (puro); acá se junta con el único paso que
// necesita red: pedirle el resumen a Gemini Flash en el backend. La función que exporta
// se llama una vez por turno, antes de la primera ronda, y NUNCA tira: si el resumen no
// sale, cae al recorte de emergencia (el plan B: se pierde el texto, se salvan los ids).

import { atlasBackendFetch } from '../../lib/atlasBackend';
import {
  aplicarCompactacion,
  bloqueDeResumen,
  cuerpoDeEmergencia,
  FRACCION_COLA,
  hayQueCompactar,
  type MensajeApi,
  presupuestoDelModelo,
  puntoDeCorte,
  transcripcionDelTramo,
} from './compactacionDeContexto';

/**
 * El resumen vigente de un chat. `hastaMensaje` es el índice (en `messages`, lo que el
 * usuario ve) del primer mensaje que TODAVÍA viaja textual: con eso, el turno siguiente
 * sabe qué parte del historial rearmado ya está cubierta.
 */
export interface EstadoDeResumen {
  hastaMensaje: number;
  /** El texto que escribió el modelo, sin el envoltorio (que se rearma con el conteo). */
  resumen: string;
  mensajesResumidos: number;
}

/** Gemini Flash con un tramo largo tarda decenas de segundos; pasado esto, plan B. */
const TIMEOUT_MS = 60_000;

export async function pedirResumen(
  transcripcion: string,
  mensajes: number,
  agentChatId: string | null,
): Promise<string> {
  const ac = new AbortController();
  const t = setTimeout(() => ac.abort(), TIMEOUT_MS);
  try {
    const res = await atlasBackendFetch(
      '/v1/agent/compactar',
      { transcripcion, mensajes, ...(agentChatId ? { agent_chat_id: agentChatId } : {}) },
      { signal: ac.signal },
    );
    if (!res.ok) throw new Error(`compactar ${res.status}`);
    const json = (await res.json()) as { resumen?: unknown };
    const resumen = typeof json.resumen === 'string' ? json.resumen.trim() : '';
    if (!resumen) throw new Error('resumen vacío');
    return resumen;
  } finally {
    clearTimeout(t);
  }
}

export interface PedidoDeCompactacion {
  /** El historial de la API. SE MUTA: el body del request lo referencia por identidad. */
  mensajes: MensajeApi[];
  /** Paralelo a `mensajes`: de qué índice de `messages` salió cada uno. También se muta. */
  origen: number[];
  model: string | null;
  resumenPrevio: EstadoDeResumen | null;
  agentChatId: string | null;
  /** Para contarle al usuario que estamos resumiendo (el turno tarda más de lo normal). */
  avisar?: (etiqueta: string | null) => void;
  /** Quién escribe el resumen. Se inyecta sólo en los tests; en la app es `pedirResumen`. */
  resumir?: (transcripcion: string, mensajes: number, agentChatId: string | null) => Promise<string>;
}

/**
 * Deja el historial en condiciones de viajar. Devuelve el estado del resumen para el
 * turno siguiente (el mismo que entró si no hizo falta tocar nada).
 *
 * Dos pasos, en este orden:
 *  1. Si ya había un resumen, se REAPLICA. El historial se rearma completo en cada turno:
 *     sin este paso, la cabeza que ya habíamos plegado volvería entera y no habríamos
 *     compactado nada.
 *  2. Si aun así se pasa del umbral, se corta más arriba y se resume el tramo nuevo. Como
 *     el tramo empieza por el resumen anterior, el resumen nuevo lo absorbe: cada
 *     compactación cuesta sólo el delta.
 */
export async function compactarHistorial(p: PedidoDeCompactacion): Promise<EstadoDeResumen | null> {
  const { mensajes, origen, model, agentChatId } = p;
  let estado = p.resumenPrevio;

  if (estado) {
    const corte = origen.findIndex((o) => o >= estado!.hastaMensaje);
    if (corte > 0) {
      aplicarCompactacion(mensajes, corte, bloqueDeResumen(estado.resumen, estado.mensajesResumidos));
      origen.splice(0, corte + 1, origen[corte]);
    } else if (corte === -1) {
      // El historial no llega hasta donde llegaba el resumen: es otro chat (o se vació).
      estado = null;
    }
  }

  if (!hayQueCompactar(mensajes, model)) return estado;

  const corte = puntoDeCorte(mensajes, presupuestoDelModelo(model) * FRACCION_COLA);
  if (corte <= 0) return estado;

  const mensajesResumidos = origen[corte];
  const transcripcion = transcripcionDelTramo(mensajes, corte);

  p.avisar?.('Resumiendo lo anterior…');
  let resumen: string;
  try {
    resumen = await (p.resumir ?? pedirResumen)(transcripcion, mensajesResumidos, agentChatId);
  } catch (e) {
    // Plan B. No se reintenta en los turnos siguientes: el chat completo sigue guardado y
    // `buscar_en_el_chat` recupera lo que haga falta, así que insistir contra un backend
    // caído sólo agregaría un minuto de espera por turno.
    console.warn('[compactar] sin resumen, recorto', e);
    resumen = cuerpoDeEmergencia(transcripcion, mensajesResumidos);
  }

  aplicarCompactacion(mensajes, corte, bloqueDeResumen(resumen, mensajesResumidos));
  origen.splice(0, corte + 1, origen[corte]);
  return { hastaMensaje: origen[0], resumen, mensajesResumidos };
}
